import * as React from "react"
import * as SelectPrimitive from "@radix-ui/react-select"
import { cva, type VariantProps } from "class-variance-authority"
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from "lucide-react"

import { cn } from "../../lib/utils"
import { formFieldBoxClass } from "./form-field-box"

const Select = SelectPrimitive.Root
const SelectGroup = SelectPrimitive.Group
const SelectValue = SelectPrimitive.Value

// `size="md"` uses the project-wide `formFieldBoxClass` so Select/Input/DatePicker are pixel-identical.
// `size="sm"` keeps the original shadcn toolbar look (h-8), unchanged for existing callers.
//
// `width` locks the trigger to a fixed pixel width so panel-filter rows don't
// reflow when the selected option changes (kind switching from "Все типы" to "Звонок"
// would otherwise shrink the trigger and shift sibling selects). Default `auto`
// keeps prior content-fit behaviour for non-filter use-cases (forms, dialogs).
const selectTriggerVariants = cva("", {
	variants: {
		size: {
			sm: "flex h-8 w-fit items-center justify-between gap-1.5 rounded-md border border-input bg-transparent px-3 py-2 text-sm whitespace-nowrap shadow-xs outline-none placeholder:text-muted-foreground focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
			md: cn(formFieldBoxClass, "justify-between whitespace-nowrap outline-none [&>span]:line-clamp-1"),
		},
		width: {
			auto: "",
			sm:   "w-[140px]",
			md:   "w-[160px]",
			lg:   "w-[200px]",
			xl:   "w-[240px]",
			full: "w-full",
		},
		tone: {
			default: "",
			// Toolbar/filter look: selected value text matches placeholder muted color.
			muted:   "text-muted-foreground",
		},
	},
	defaultVariants: { size: "sm", width: "auto", tone: "default" },
})

export type SelectTriggerProps = React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> &
	VariantProps<typeof selectTriggerVariants>

function SelectTrigger({
	className,
	size,
	width,
	tone,
	children,
	...props
}: SelectTriggerProps) {
	// Render Radix trigger as a `<div>` (not its default `<button>`) via `asChild`.
	// This eliminates user-agent button styling quirks (line-height: normal, font: inherit
	// edge-cases) that would make the trigger 1–2px off from `<Input>` in the same row.
	// Radix still manages keyboard + ARIA on the div.
	return (
		<SelectPrimitive.Trigger asChild {...props}>
			<div
				role="combobox"
				tabIndex={props.disabled ? -1 : 0}
				aria-disabled={props.disabled || undefined}
				className={cn(
					selectTriggerVariants({ size, width, tone, className }),
					props.disabled
						? 'cursor-not-allowed opacity-50 pointer-events-none'
						: 'cursor-pointer',
				)}
			>
				{children}
				<SelectPrimitive.Icon asChild>
					<ChevronDownIcon className="size-4 opacity-50" />
				</SelectPrimitive.Icon>
			</div>
		</SelectPrimitive.Trigger>
	)
}

function SelectScrollUpButton({
	className,
	...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>) {
	return (
		<SelectPrimitive.ScrollUpButton
			className={cn("flex cursor-default items-center justify-center py-1", className)}
			{...props}
		>
			<ChevronUpIcon className="size-4" />
		</SelectPrimitive.ScrollUpButton>
	)
}

function SelectScrollDownButton({
	className,
	...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>) {
	return (
		<SelectPrimitive.ScrollDownButton
			className={cn("flex cursor-default items-center justify-center py-1", className)}
			{...props}
		>
			<ChevronDownIcon className="size-4" />
		</SelectPrimitive.ScrollDownButton>
	)
}

function SelectContent({
	className,
	children,
	position = "popper",
	...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>) {
	return (
		<SelectPrimitive.Portal>
			<SelectPrimitive.Content
				className={cn(
					"relative z-50 max-h-96 min-w-[8rem] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
					position === "popper" &&
						"data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
					className
				)}
				position={position}
				{...props}
			>
				<SelectScrollUpButton />
				<SelectPrimitive.Viewport
					className={cn(
						"p-1",
						position === "popper" && "h-[var(--radix-select-trigger-height)] w-full min-w-[var(--radix-select-trigger-width)]"
					)}
				>
					{children}
				</SelectPrimitive.Viewport>
				<SelectScrollDownButton />
			</SelectPrimitive.Content>
		</SelectPrimitive.Portal>
	)
}

function SelectLabel({
	className,
	...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>) {
	return (
		<SelectPrimitive.Label
			className={cn("py-1.5 pr-2 pl-8 text-sm font-semibold", className)}
			{...props}
		/>
	)
}

function SelectItem({
	className,
	children,
	...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>) {
	return (
		<SelectPrimitive.Item
			className={cn(
				"relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pr-2 pl-8 text-sm outline-none focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
				className
			)}
			{...props}
		>
			<span className="absolute left-2 flex size-3.5 items-center justify-center">
				<SelectPrimitive.ItemIndicator>
					<CheckIcon className="size-4" />
				</SelectPrimitive.ItemIndicator>
			</span>
			<SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
		</SelectPrimitive.Item>
	)
}

function SelectSeparator({
	className,
	...props
}: React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>) {
	return (
		<SelectPrimitive.Separator
			className={cn("-mx-1 my-1 h-px bg-muted", className)}
			{...props}
		/>
	)
}

export {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectLabel,
	SelectScrollDownButton,
	SelectScrollUpButton,
	SelectSeparator,
	SelectTrigger,
	SelectValue,
}
